import { z } from 'zod';

/**
 * Skema baris impor massal admin (member & grant membership).
 *
 * Alurnya sama dengan impor soal tryout (lihat exam-import.ts): CSV diparse di
 * BROWSER dengan papaparse, divalidasi zod di klien untuk pratinjau bertanda
 * error, lalu dikirim sebagai `{rows: [...]}` — file mentah tidak pernah
 * diunggah. Server memvalidasi ULANG per baris, melewati yang gagal dan
 * melaporkannya, bukan menggagalkan seluruh berkas.
 */

/** Sama dengan impor soal — cukup untuk satu angkatan, aman untuk satu request Worker. */
export const IMPORT_MAX_ROWS = 500;

const emptyToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

// ─── Impor member ────────────────────────────────────────────────────────────

// Header template CSV: public/templates/template-member.csv
export const MEMBER_IMPORT_HEADERS = ['name', 'email', 'phone', 'password'] as const;

export const memberImportRowSchema = z.object({
  name: z.string().min(1, 'name wajib diisi').max(120).transform((s) => s.trim()),
  email: z
    .string()
    .min(1, 'email wajib diisi')
    .max(254)
    .email('Format email tidak valid')
    .transform((s) => s.trim().toLowerCase()),
  // Nomor WhatsApp. Tidak wajib: baris tanpa nomor tetap bisa diimpor, dan
  // nomor yang bentrok dengan akun lain disimpan NULL (lihat catatan di route).
  phone: z.preprocess(emptyToUndefined, z.string().max(24).optional()),
  // Kosong → server membuat password acak kuat; member masuk lewat magic link
  // / OTP, pola yang sama dengan checkout tamu.
  password: z.preprocess(emptyToUndefined, z.string().min(8, 'password minimal 8 karakter').max(72).optional()),
});

export type MemberImportRow = z.infer<typeof memberImportRowSchema>;

// ─── Impor grant membership ──────────────────────────────────────────────────

// Header template CSV: public/templates/template-grant-membership.csv
export const GRANT_IMPORT_HEADERS = ['email', 'plan_slug'] as const;

export const grantImportRowSchema = z.object({
  email: z
    .string()
    .min(1, 'email wajib diisi')
    .max(254)
    .email('Format email tidak valid')
    .transform((s) => s.trim().toLowerCase()),
  plan_slug: z.string().min(1, 'plan_slug wajib diisi').max(120).transform((s) => s.trim().toLowerCase()),
});

export type GrantImportRow = z.infer<typeof grantImportRowSchema>;

// ─── Laporan hasil ───────────────────────────────────────────────────────────

export interface ImportRowError {
  /** 1-based, sesuai urutan baris dikirim — supaya admin bisa mencarinya di file. */
  row: number;
  message: string;
}

/**
 * Pembungkus body request. Sama bentuknya dengan importQuestionBankSchema —
 * baris divalidasi satu per satu di handler, bukan di sini, supaya satu baris
 * rusak tidak menolak seluruh berkas.
 */
export const bulkImportSchema = z.object({
  rows: z
    .array(z.unknown())
    .min(1, 'rows kosong')
    .max(IMPORT_MAX_ROWS, `Maksimal ${IMPORT_MAX_ROWS} baris per impor`),
});

export type BulkImportInput = z.infer<typeof bulkImportSchema>;

export interface ImportReport {
  /** Baris yang benar-benar menghasilkan record baru. */
  inserted: number;
  /** Baris valid yang sengaja dilewati (mis. email sudah terdaftar). */
  skipped: number;
  errors: ImportRowError[];
  /**
   * Peringatan non-fatal: barisnya masuk, tapi tidak persis seperti isi file.
   * Dipakai untuk nomor telepon bentrok yang terpaksa dikosongkan — kalau ini
   * dilaporkan sebagai error, admin mengira barisnya gagal padahal masuk.
   */
  warnings: ImportRowError[];
}
